import { bootstrapApplication } from '@angular/platform-browser';
import { appConfig } from './app/app.config';
import { AppComponent } from './app/app.component';
import 'core-js/modules/es.promise.with-resolvers';
import { environment } from '@src/environments/environment';
import { detectPlatformFromHostname } from '@app/helpers/platform';
import { hasConsent } from '@app/helpers/consent';

const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
const platform = detectPlatformFromHostname(hostname);
const newrelic = environment.newrelic[platform] || environment.newrelic.mobiliar;

type EarlyCapturedError = {
  error: Error;
  customAttributes?: Record<string, string | number | boolean>;
};

async function initializeNewRelic() {
  const earlyCapturedErrors: EarlyCapturedError[] = [];

  const onWindowError = (event: ErrorEvent) => {
    const fallback = new Error(event.message || 'Unknown window error');
    const error = event.error instanceof Error ? event.error : fallback;

    earlyCapturedErrors.push({
      error,
      customAttributes: {
        filename: event.filename || 'unknown',
        lineno: event.lineno || 0,
        colno: event.colno || 0,
        source: 'window.error',
      },
    });
  };

  const onUnhandledRejection = (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    const error =
      reason instanceof Error
        ? reason
        : new Error(typeof reason === 'string' ? reason : 'Unhandled promise rejection');

    earlyCapturedErrors.push({
      error,
      customAttributes: {
        source: 'window.unhandledrejection',
      },
    });
  };

  window.addEventListener('error', onWindowError);
  window.addEventListener('unhandledrejection', onUnhandledRejection);

  if (
    !newrelic.accountId ||
    !newrelic.trustKey ||
    !newrelic.agentId ||
    !newrelic.licenseKey ||
    !newrelic.applicationId
  ) {
    window.removeEventListener('error', onWindowError);
    window.removeEventListener('unhandledrejection', onUnhandledRejection);
    return;
  }

  try {
    const { BrowserAgent } =
      await import('@newrelic/browser-agent/loaders/browser-agent');

    new BrowserAgent({
      init: {
        browser_consent_mode: { enabled: true },
        distributed_tracing: { enabled: true },
        privacy: { cookies_enabled: true },
        ajax: { deny_list: ['bam.eu01.nr-data.net'] },
      },
      info: {
        beacon: 'bam.eu01.nr-data.net',
        errorBeacon: 'bam.eu01.nr-data.net',
        licenseKey: newrelic.licenseKey,
        applicationID: newrelic.applicationId,
        sa: 1,
      },
      loader_config: {
        accountID: newrelic.accountId,
        trustKey: newrelic.trustKey,
        agentID: newrelic.agentId,
        licenseKey: newrelic.licenseKey,
        applicationID: newrelic.applicationId,
      },
    });

    const nrApi = (window as any).newrelic;
    if (nrApi?.noticeError) {
      earlyCapturedErrors.forEach(({ error, customAttributes }) => {
        nrApi.noticeError(error, customAttributes);
      });
    }

    const consent = () => {
      if (typeof nrApi?.consent === 'function') {
        nrApi.consent(hasConsent('C0002'));
      }
    };

    window.addEventListener('OneTrustGroupsUpdated', () => {
      consent();
    });
  } catch {
    // Ignore monitoring initialization failures in client runtime.
  } finally {
    window.removeEventListener('error', onWindowError);
    window.removeEventListener('unhandledrejection', onUnhandledRejection);
  }
}

const hasNewRelicConfig =
  !!newrelic.accountId &&
  !!newrelic.trustKey &&
  !!newrelic.agentId &&
  !!newrelic.licenseKey &&
  !!newrelic.applicationId;

if (hasNewRelicConfig) {
  void initializeNewRelic();
}
bootstrapApplication(AppComponent, appConfig).catch((err) => console.error(err));
